// Central catalog mapping backend error codes (and transport conditions) to short, friendly,
// actionable messages. Technical codes (VALIDATION_FAILED, FOREIGN_KEY_CONSTRAINT, …) must never
// reach the user — they are translated here. App-specific codes that already carry a friendly
// backend message are intentionally omitted so their specific message is shown instead.

export const ERROR_MESSAGES: Record<string, string> = {
  // Validation
  VALIDATION_FAILED: 'Please check the highlighted fields and try again.',
  VALIDATION_ERROR: 'Please check the highlighted fields and try again.',
  EMAIL_REQUIRED: 'Please enter an email address.',
  PHONE_REQUIRED: 'Please enter a phone number.',

  // Conflicts / duplicates
  DUPLICATE_EMAIL: 'This email is already registered.',
  EMAIL_EXISTS: 'This email is already registered.',
  EMAIL_ALREADY_EXISTS: 'This email is already registered. Please sign in or use a different email.',
  MOBILE_ALREADY_EXISTS: 'This mobile number is already registered. Please sign in or use a different number.',
  SETUP_INCOMPLETE_EXISTS: 'An incomplete setup already exists for this account. Resume setup?',
  DUPLICATE_SUBDOMAIN: 'This company URL is already in use.',
  SUBDOMAIN_EXISTS: 'This company URL is already in use.',
  CONFLICT: 'This conflicts with something that already exists.',
  DUPLICATE: 'This already exists.',

  // Access / lookup
  PASSWORD_CHANGE_REQUIRED: 'Please change your temporary password to continue.',
  NOT_FOUND: 'We couldn’t find what you were looking for.',
  UNAUTHORIZED: 'You do not have permission to perform this action.',
  FORBIDDEN: 'Access denied.',

  // Transport
  NETWORK_ERROR: 'Unable to connect. Please check your internet connection.',
  TIMEOUT: 'The request took too long. Please try again.',
  SERVER_ERROR: 'The server ran into a problem and could not finish this. Please try again.',
  INTERNAL_SERVER_ERROR: 'The server ran into a problem and could not finish this. Please try again.',
  INTERNAL_ERROR: 'The server ran into a problem and could not finish this. Please try again.',
  FOREIGN_KEY_CONSTRAINT: 'This record is in use and cannot be changed.',
  DATA_CONFLICT: 'This record is in use elsewhere and can’t be changed.',
  NULL_REFERENCE_EXCEPTION: 'The server ran into a problem and could not finish this. Please try again.',
  DATABASE_UNAVAILABLE: 'The database could not be reached just now. Please try again in a minute.',
  PAYLOAD_TOO_LARGE: 'The file or data you sent is too large. Please upload a smaller file.',
};

/** Generic, safe message for any duplicate/conflict the backend can't describe specifically (Phase 8). */
export const DUPLICATE_FALLBACK = 'A record with this information already exists.';

/**
 * Friendly, field-level messages (Phase 4). Raw backend/validator wording (e.g. FluentValidation's
 * "'Email' is not a valid email address.") is normalized here to calm, actionable copy shown beneath
 * the field. The same patterns drive Zod messages in {@link '@/utils/validation'} so the two ends
 * read identically. Order matters — first match wins.
 */
/**
 * Validator wording rewritten into plain words. Applied ONLY to the validator's stock text (which names the
 * field in quotes, e.g. "'Bank Name' must not be empty."); messages the app wrote itself are already plain and
 * are shown exactly as written. `{n}` is the number found in the original text, when there is one.
 */
const FIELD_MESSAGE_RULES: Array<{ test: RegExp; message: string }> = [
  { test: /valid email|not a valid e-?mail/i, message: 'enter a valid email address, e.g. name@company.com.' },
  { test: /valid phone|phone.*(invalid|not valid|format)/i, message: 'enter a valid phone number.' },
  { test: /valid date|date.*(invalid|not valid|format)/i, message: 'choose a valid date.' },
  { test: /must not be empty|is required|cannot be empty|must be provided|must not be null/i, message: 'please fill this in.' },
  { test: /length of .* must be at least|must be at least \d+ characters/i, message: 'too short — use at least {n} characters.' },
  { test: /length of .* must be|or fewer|too long|maximum length/i, message: 'too long — use at most {n} characters.' },
  { test: /must be greater than or equal to/i, message: 'must be {n} or more.' },
  { test: /must be greater than/i, message: 'must be more than {n}.' },
  { test: /must be less than or equal to/i, message: 'must be {n} or less.' },
  { test: /must be less than/i, message: 'must be less than {n}.' },
  { test: /must be between|inclusive/i, message: 'is outside the allowed range.' },
  { test: /is not in the correct format|does not match|is not valid|has a range of values/i, message: 'is not in the right format. Please check it.' },
  { test: /already (exists|registered|in use)|duplicate/i, message: 'already exists.' },
];

/** "AccountHolder" / "Account Holder" / "accountHolder" → "Account holder". */
function fieldLabel(name: string): string {
  const words = name.replace(/[_.]/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2').trim().toLowerCase();
  return words ? words[0].toUpperCase() + words.slice(1) : '';
}

/**
 * Converts one server validation message into plain words a person can act on, always naming the field so
 * it makes sense even when shown away from the field (in the box at the top of a form or in a pop-up).
 * `field` is the property the server reported it against, when known.
 */
export function friendlyFieldMessage(raw: string | null | undefined, field?: string): string {
  const m = (raw ?? '').trim();
  const quoted = m.match(/['‘’"“”]([^'‘’"“”]+)['‘’"“”]/)?.[1];
  const label = fieldLabel(quoted ?? field ?? '');
  if (!m) return label ? `${label}: please check this.` : 'Please check the highlighted field.';

  // Stock validator text names the field in quotes: rewrite it plainly, keeping the field's name.
  if (quoted) {
    const hit = FIELD_MESSAGE_RULES.find((r) => r.test.test(m));
    const n = m.match(/\d+/)?.[0] ?? '';
    if (hit) return `${label}: ${hit.message.replace('{n}', n)}`;
    return `${label}: please check this.`;
  }

  // Written by the app (e.g. "Enter the IFSC (India) or the SWIFT code (abroad).") — already plain.
  if (isFriendlyText(m)) return m;
  return label ? `${label}: please check this.` : 'Please check the highlighted field.';
}

// Local guard mirroring errorMessage.ts so the catalog has no import cycle.
function isFriendlyText(msg: string): boolean {
  const technical = [
    /\bexception\b/i, /stack\s*trace/i, /System\.[A-Za-z]/, /null reference/i,
    /^[A-Z][A-Z0-9_]{2,}$/, /\bat\s+[A-Za-z][\w.]+\(/, /cannot read propert/i,
  ];
  return !technical.some((re) => re.test(msg));
}

/** Friendly fallback by HTTP status when no error code matches. */
export const STATUS_MESSAGES: Record<number, string> = {
  0: 'Unable to connect. Please check your internet connection.',
  400: 'Please check the information you entered and try again.',
  401: 'Your session has expired. Please sign in again.',
  403: 'You don’t have permission to do this. Please contact your administrator.',
  404: 'We couldn’t find what you were looking for.',
  408: 'The request took too long. Please try again.',
  409: 'This conflicts with something that already exists.',
  422: 'Please check the information you entered and try again.',
  405: 'This action is not available here. Please refresh the page and try again.',
  413: 'The file or data you sent is too large. Please upload a smaller file.',
  415: 'This type of file or data is not accepted.',
  429: 'Too many requests. Please wait a moment and try again.',
  500: 'The server ran into a problem and could not finish this. Please try again.',
  502: 'An outside service could not be reached. Please try again in a few minutes.',
  503: 'The service is temporarily unavailable. Please try again shortly.',
  504: 'The request took too long. Please try again.',
};
